import React, { FC } from 'react';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { MdAdd, MdClose } from 'react-icons/md';
import { useQuery } from 'react-query';

import { fetchArts } from 'api/art';
import FormikInput from 'components/primitives/FormikInput';

import { ManufacturerDetailsValues } from './SkuManufacturerDetails.utils';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

/** The editable tiers of one cost-per-MOQ field. */
const MoqTable: FC<{
  name: 'manufacturerBoxCostPerMoq' | 'manufacturerUnitCostPerMoq';
  label: string;
  moqLabel: string;
  priceLabel: string;
}> = ({ name, label, moqLabel, priceLabel }) => {
  const { values, errors } = useFormikContext<ManufacturerDetailsValues>();
  const rows = values[name] || [];
  const tableError = getIn(errors, name);

  return (
    <Box flex={1}>
      <Text {...labelProps} fontWeight="600" mb={1}>
        {label}
      </Text>
      <FieldArray name={name}>
        {({ push, remove }) => (
          <Flex direction="column">
            {rows.map((_, index) => (
              <Flex key={index} gap="8px" align="flex-start">
                <FormikInput
                  name={`${name}.${index}.moq`}
                  aria-label={`${label} ${moqLabel} ${index + 1}`}
                  placeholder={moqLabel}
                  inputMode="numeric"
                  containerProps={{ flex: 1 }}
                />
                <FormikInput
                  name={`${name}.${index}.price`}
                  aria-label={`${label} ${priceLabel} ${index + 1}`}
                  placeholder={priceLabel}
                  inputMode="decimal"
                  containerProps={{ flex: 1 }}
                />
                <IconButton
                  mt={3}
                  aria-label={`Remove ${label} tier ${index + 1}`}
                  variant="ghost"
                  icon={<MdClose />}
                  onClick={() => remove(index)}
                />
              </Flex>
            ))}
            {typeof tableError === 'string' ? (
              <Text color="red.500" fontSize="sm" mt={1}>
                {tableError}
              </Text>
            ) : null}
            <Button
              mt={3}
              alignSelf="flex-start"
              size="sm"
              variant="outline"
              leftIcon={<MdAdd />}
              onClick={() => push({ moq: '', price: '' })}>
              Add tier
            </Button>
          </Flex>
        )}
      </FieldArray>
    </Box>
  );
};

/**
 * Manufacturer details on an item SKU (ClickUp 86bcg0wkv), rendered inside
 * the Add and Edit SKU forms. Art numbers come from arts_mst.
 */
const SkuManufacturerDetails: FC = () => {
  const {
    values,
    setFieldValue,
  } = useFormikContext<ManufacturerDetailsValues>();

  const { data: arts = [], isLoading: isLoadingArts } = useQuery(
    ['fetch-arts'],
    fetchArts,
  );

  // Stored by art number, as purchase orders look them up; labelled with the
  // description when there is one.
  const artLabel = (number: string) => {
    const art = arts.find((a) => a.number === number);
    return art?.description ? `${art.description} (${number})` : number;
  };
  const options = arts.map((art) => ({
    label: artLabel(art.number),
    value: art.number,
  }));
  const selected = (values.artNumbers || []).map((number) => ({
    label: artLabel(number),
    value: number,
  }));

  return (
    <Box mt={8}>
      <Text fontWeight="600" color="support.500" mb={2}>
        Manufacturer details
      </Text>
      <FormControl mt={3}>
        <FormLabel htmlFor="artNumbers" {...labelProps}>
          Art Numbers
        </FormLabel>
        <Select
          inputId="artNumbers"
          isMulti
          options={options}
          value={selected}
          isLoading={isLoadingArts}
          placeholder="Select art"
          tagVariant="solid"
          colorScheme="primary"
          closeMenuOnSelect={false}
          onChange={(next) =>
            setFieldValue(
              'artNumbers',
              next.map((option: any) => option.value),
            )
          }
        />
      </FormControl>
      <Flex mt={3}>
        <FormikInput
          name="manufacturerSkuId"
          label="Manufacturer SKU ID"
          labelProps={labelProps}
          placeholder="Your text here"
        />
        <Box h="20px" w="50px" />
        <FormikInput
          name="manufacturerDescription"
          label="Manufacturer Description"
          labelProps={labelProps}
          placeholder="Your text here"
        />
      </Flex>
      <Flex mt={3}>
        <FormikInput
          name="manufacturerItemsInBox"
          label="Items In Box"
          labelProps={labelProps}
          placeholder="0"
          inputMode="numeric"
        />
        <Box h="20px" w="50px" />
        <FormikInput
          name="manufacturerPalletCount"
          label="Pallet Count"
          labelProps={labelProps}
          placeholder="0"
          inputMode="numeric"
        />
      </Flex>
      <Flex mt={6} gap="50px">
        <MoqTable
          name="manufacturerBoxCostPerMoq"
          label="Box Cost Per MOQ"
          moqLabel="Min boxes"
          priceLabel="Price per box"
        />
        <MoqTable
          name="manufacturerUnitCostPerMoq"
          label="Unit Cost Per MOQ"
          moqLabel="Min units"
          priceLabel="Price per unit"
        />
      </Flex>
    </Box>
  );
};

export default SkuManufacturerDetails;
